import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { useShallow } from 'zustand/react/shallow';

import { useFetchRoles, useSyncGuardianProfile } from '@/features/auth/queries';
import { getFirebaseApp } from '@/libs/firebase/init.web';
import { captureException } from '@/libs/logger';
import { ensureOnline } from '@/libs/network';
import { useSessionStore } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';

/**
 * Web variant of {@link useGoogleLogin}. The native One Tap SDK
 * (`react-native-nitro-google-signin`) is a Nitro TurboModule with no web
 * build, so on web we sign in through Firebase's OAuth popup with
 * `GoogleAuthProvider`. Firebase uses the OAuth web client configured in the
 * Firebase Console, so no client id is needed here.
 */
export const useGoogleLogin = () => {
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const setIsPendingLoginSocial = useSessionStore(useShallow(s => s.setIsPendingLoginSocial));
  const { mutateAsync: syncGuardianProfile } = useSyncGuardianProfile();
  const { mutateAsync: fetchRoles } = useFetchRoles();

  const resolveRolesThenUnlock = async () => {
    const { roles } = await fetchRoles();
    useSessionStore.getState().setRoles(roles);
    useSessionStore.getState().setActiveRole(roles?.[0] ?? null);
    useSessionStore.getState().setSoftLoggedOut(false);
  };

  const handleGoogleLogin = async () => {
    showGlobalLoading();
    setIsPendingLoginSocial(true);
    try {
      await ensureOnline();

      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');

      // Popup is user-driven, so no request timeout is applied here.
      const result = await signInWithPopup(getAuth(getFirebaseApp()), provider);

      const displayName = result.user.displayName?.trim() ?? '';
      const [firstname = '', ...rest] = displayName.split(/\s+/).filter(Boolean);
      const lastname = rest.join(' ');

      await syncGuardianProfile({
        data: {
          firstname,
          lastname,
          photoUrl: result.user.photoURL ?? '',
        },
      });

      // Resolve THIS account's roles into the store BEFORE clearing the soft-logout lock.
      // Clearing the lock is what flips the withUnAuth gate (the whole UnAuth stack is wrapped
      // in it) and fires resetRootToPostAuthTarget, which routes purely from the store's roles.
      // Soft logout keeps the *previous* account warm (roles/activeRole/isAuthenticated intact),
      // so unlocking first would route from the old roles — e.g. a coach who soft-logged-out
      // then signed in as a guardian would land on the coach dashboard. Resolve, then unlock.
      await resolveRolesThenUnlock();
    } catch (error) {
      captureException(
        {
          error_payload: error,
        },
        { tags: { usk_log_type: 'google_login_error' } },
      );
    } finally {
      hideGlobalLoading();
      setIsPendingLoginSocial(false);
    }
  };

  return {
    handleGoogleLogin,
  };
};
